import React, { useMemo } from 'react';
import {
  Play, FileCheck2, ScanSearch, PenTool, Send, ShieldCheck,
  Check, AlertTriangle, Sparkles, UploadCloud, ExternalLink,
} from 'lucide-react';
import { useLang } from '../context/LanguageContext';

/**
 * The 17 underlying workflow stages roll up into 6 user-facing phases.
 * Order matters: this drives the horizontal stepper order.
 */
export const PHASES = [
  {
    key: 'intake',
    labelKey: 'tracker.p.intake',
    descKey: 'tracker.p.intake.desc',
    icon: Play,
    stage_codes: ['intake_started', 'intake_completed', 'specialist_assigned'],
  },
  {
    key: 'documents',
    labelKey: 'tracker.p.documents',
    descKey: 'tracker.p.documents.desc',
    icon: FileCheck2,
    stage_codes: ['documents_requested', 'documents_received'],
  },
  {
    key: 'review',
    labelKey: 'tracker.p.review',
    descKey: 'tracker.p.review.desc',
    icon: ScanSearch,
    stage_codes: [
      'documents_under_review', 'additional_info_requested',
      'awaiting_client_response', 'preparation_in_progress',
      'internal_review_complete',
    ],
  },
  {
    key: 'signature',
    labelKey: 'tracker.p.signature',
    descKey: 'tracker.p.signature.desc',
    icon: PenTool,
    stage_codes: ['signature_requested', 'awaiting_signature'],
  },
  {
    key: 'filed_ready',
    labelKey: 'tracker.p.filed_ready',
    descKey: 'tracker.p.filed_ready.desc',
    icon: Send,
    stage_codes: ['ready_to_file'],
  },
  {
    key: 'cra',
    labelKey: 'tracker.p.cra',
    descKey: 'tracker.p.cra.desc',
    icon: ShieldCheck,
    stage_codes: [
      'submitted_to_cra', 'cra_confirmation_received',
      'completed', 'archived',
    ],
  },
];

/** Map a stage_code → phase index. */
function phaseIndexFor(stageCode) {
  if (!stageCode) return 0;
  const idx = PHASES.findIndex((p) => p.stage_codes.includes(stageCode));
  return idx === -1 ? 0 : idx;
}

function stageByCode(stages, code) {
  return stages?.find((s) => s.code === code);
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

/* ------------------------------------------------------------------ Step dot */
function StepDot({ status, IconCmp }) {
  const base = 'w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center shrink-0 border-[3px] transition-all duration-300';
  if (status === 'completed') {
    return (
      <div className={`${base} bg-emerald-500 border-emerald-600 text-white shadow-[0_6px_20px_-6px_rgba(16,185,129,0.55)]`}>
        <Check className="w-5 h-5" strokeWidth={3} />
      </div>
    );
  }
  if (status === 'attention') {
    return (
      <div className={`${base} bg-[#f5b300] border-[#e0a200] text-[#14110f] shadow-[0_6px_24px_-6px_rgba(245,179,0,0.7)] animate-pulse-soft motion-reduce:animate-none`}>
        <AlertTriangle className="w-5 h-5" strokeWidth={2.5} />
      </div>
    );
  }
  if (status === 'current') {
    return (
      <div className={`${base} bg-[#0ea5e9] border-[#0284c7] text-white shadow-[0_6px_24px_-6px_rgba(14,165,233,0.6)] animate-pulse-soft motion-reduce:animate-none`}>
        {IconCmp ? <IconCmp className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
      </div>
    );
  }
  return (
    <div className={`${base} bg-white border-neutral-200 text-neutral-300`}>
      {IconCmp ? <IconCmp className="w-5 h-5" /> : null}
    </div>
  );
}

/* ============================================================ Stepper */
export default function TaxFilingProgressStepper({
  timeline,
  loading = false,
  onAction = null,
  className = '',
}) {
  const { t } = useLang();
  // Decorate phases with status, timestamp, and notes drawn from underlying stages.
  const { phases, currentPhaseIdx, pct } = useMemo(() => {
    const stages = timeline?.stages || [];
    const events = timeline?.events || [];
    const curIdx = phaseIndexFor(timeline?.current_stage_code);
    const curStage = stageByCode(stages, timeline?.current_stage_code);

    const decorated = PHASES.map((p, i) => {
      // Aggregate events whose stage falls in this phase.
      const phaseEvents = events.filter((e) => p.stage_codes.includes(e.stage_code));
      const enteredFirst = phaseEvents.find((e) => e.event_type === 'entered');
      const notes = phaseEvents.filter((e) => e.event_type === 'note');
      let status;
      if (i < curIdx) status = 'completed';
      else if (i === curIdx) status = curStage?.requires_client_action ? 'attention' : 'current';
      else status = 'pending';
      return {
        ...p,
        label: t(p.labelKey),
        description: t(p.descKey),
        status,
        entered_at: enteredFirst?.created_at || null,
        actor: enteredFirst?.actor_name || '',
        notes,
        active_stage: i === curIdx ? curStage : null,
      };
    });

    const completedCount = decorated.filter((p) => p.status === 'completed').length;
    const isLastDone = decorated[decorated.length - 1]?.status === 'completed';
    const filled = isLastDone
      ? 100
      : ((completedCount + (decorated[curIdx]?.status === 'pending' ? 0 : 0.5)) / decorated.length) * 100;
    return { phases: decorated, currentPhaseIdx: curIdx, pct: Math.min(100, Math.round(filled)) };
  }, [timeline?.current_stage_code, timeline?.events, timeline?.stages, t]);

  if (loading) {
    return (
      <div className={`rounded-2xl border border-neutral-100 bg-white p-6 ${className}`} data-testid="timeline-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-neutral-100 rounded w-2/3" />
          <div className="h-2 bg-neutral-100 rounded w-full" />
          <div className="flex justify-between gap-3 pt-4">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-full bg-neutral-100" />
                <div className="h-3 bg-neutral-100 rounded w-3/4" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!timeline) {
    return (
      <div className={`rounded-2xl border border-neutral-100 bg-white p-6 text-center text-neutral-500 ${className}`} data-testid="timeline-empty">
        {t('tracker.noFile')}
      </div>
    );
  }

  const current = phases[currentPhaseIdx];
  const stages = timeline?.stages || [];
  const currentStage = stageByCode(stages, timeline?.current_stage_code);

  return (
    <div className={`rounded-2xl border border-neutral-100 bg-white shadow-[0_25px_60px_-35px_rgba(20,17,15,0.25)] overflow-hidden ${className}`} data-testid="tax-filing-tracker">
      {/* Header */}
      <div className="px-6 pt-6 pb-5 bg-gradient-to-br from-[#14110f] to-[#291d12] text-white">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#f5b300] font-bold mb-1">{t('tracker.eyebrow')}</p>
            <h2 className="text-[20px] md:text-[22px] font-extrabold leading-tight text-white">{current.label}</h2>
            <p className="text-[13px] text-white/70 mt-1.5 max-w-xl">
              {current.description}
              {timeline.assigned_specialist?.name ? (
                <> {t('tracker.specialistPrefix')} <span className="text-white font-semibold">{timeline.assigned_specialist.name}</span>.</>
              ) : null}
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/60 font-bold">{t('tracker.taxYear')}</p>
            <p className="text-[24px] font-extrabold text-[#f5b300]">{timeline.tax_year}</p>
          </div>
        </div>

        {/* Slim progress bar */}
        <div className="mt-5">
          <div className="flex items-center justify-between text-[11px] text-white/70 mb-1.5">
            <span className="font-semibold uppercase tracking-wider">{t('tracker.step')} {currentPhaseIdx + 1} {t('tracker.of')} {phases.length}</span>
            <span className="text-[#f5b300] font-bold">{pct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#f5b300] to-[#e0a200] transition-all duration-700"
                  style={{ width: `${pct}%` }} aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* Horizontal stepper — desktop */}
      <div className="hidden md:block px-6 pt-7 pb-6">
        <ol className="relative grid grid-cols-6 gap-3" role="list">
          {/* Background track */}
          <div className="absolute top-[22px] left-[8.333%] right-[8.333%] h-[3px] bg-neutral-200 rounded-full" aria-hidden="true" />
          {/* Progress fill */}
          <div
            className="absolute top-[22px] left-[8.333%] h-[3px] bg-gradient-to-r from-emerald-500 via-[#0ea5e9] to-[#f5b300] rounded-full transition-all duration-700"
            style={{ width: `${(Math.max(0, (currentPhaseIdx) / (phases.length - 1)) * (100 - 16.666)).toFixed(2)}%` }}
            aria-hidden="true"
          />
          {phases.map((p, i) => (
            <li key={p.key} className="relative flex flex-col items-center text-center" data-testid={`phase-step-${p.key}`}>
              <StepDot status={p.status} IconCmp={p.icon} />
              <p className={`mt-3 text-[12.5px] font-bold leading-tight ${
                p.status === 'completed' ? 'text-emerald-700'
                  : p.status === 'attention' ? 'text-[#7c5a00]'
                    : p.status === 'current' ? 'text-[#0369a1]'
                      : 'text-neutral-400'}`}>
                {p.label}
              </p>
              {p.status === 'completed' && p.entered_at && (
                <p className="text-[10.5px] text-neutral-400 mt-0.5">{fmtDate(p.entered_at)}</p>
              )}
              {p.status === 'attention' && (
                <span className="mt-1 inline-flex text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#f5b300] text-[#14110f]">{t('tracker.actionPill')}</span>
              )}
              {p.status === 'current' && (
                <span className="mt-1 inline-flex text-[9.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#0ea5e9] text-white">{t('tracker.inProgress')}</span>
              )}
            </li>
          ))}
        </ol>
      </div>

      {/* Vertical stepper — mobile fallback */}
      <ol className="md:hidden px-5 pt-6 pb-2 space-y-0" role="list">
        {phases.map((p, i) => (
          <li key={p.key} className="relative flex gap-3" data-testid={`phase-step-mobile-${p.key}`}>
            <div className="flex flex-col items-center shrink-0">
              <StepDot status={p.status} IconCmp={p.icon} />
              {i < phases.length - 1 && (
                <div className={`flex-1 w-[3px] my-1 rounded-full ${
                  p.status === 'completed' ? 'bg-emerald-500'
                    : 'bg-neutral-200'}`} aria-hidden="true" />
              )}
            </div>
            <div className="pb-5 flex-1 min-w-0">
              <p className={`text-[14px] font-bold ${
                p.status === 'completed' ? 'text-emerald-700'
                  : p.status === 'attention' ? 'text-[#7c5a00]'
                    : p.status === 'current' ? 'text-[#0369a1]'
                      : 'text-neutral-400'}`}>{p.label}</p>
              <p className="text-[12px] text-neutral-500 mt-0.5">{p.description}</p>
              {p.status === 'completed' && p.entered_at && (
                <p className="text-[11px] text-neutral-400 mt-1">{t('tracker.completed')} {fmtDate(p.entered_at)}</p>
              )}
            </div>
          </li>
        ))}
      </ol>

      {/* Active phase detail card */}
      <div className="px-5 md:px-6 pb-6">
        <div className={`rounded-xl p-4 md:p-5 border ${
          current.status === 'attention'
            ? 'bg-gradient-to-br from-[#fff8e3] to-white border-[#f5b300]/60'
            : current.status === 'completed'
              ? 'bg-emerald-50/30 border-emerald-200'
              : 'bg-gradient-to-br from-[#f0f9ff] to-white border-[#0ea5e9]/30'
        }`} data-testid="active-phase-detail">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold mb-1">{t('tracker.whereYouAre')}</p>
              <h3 className="text-[16px] font-bold text-[#14110f]">{current.label}</h3>
              {currentStage && currentStage.code !== current.stage_codes[0] && (
                <p className="text-[12px] text-neutral-500 mt-0.5">{t('tracker.currently')} <span className="font-semibold text-neutral-700">{currentStage.name}</span></p>
              )}
              <p className="text-[13px] text-neutral-700 mt-2 max-w-3xl">
                {currentStage?.description || current.description}
              </p>
              {current.notes?.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  <p className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{t('tracker.specialistNotes')}</p>
                  {current.notes.slice(-3).map((n) => (
                    <div key={n.id} className="rounded-md bg-white border border-neutral-100 px-3 py-2">
                      <p className="text-[11px] text-neutral-500 mb-0.5">{n.actor_name || 'Specialist'} · {fmtDate(n.created_at)}</p>
                      <p className="text-[13px] text-neutral-800 whitespace-pre-line">{n.note}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* CTAs for the active phase */}
          {onAction && (current.status === 'attention' || current.status === 'current') && (
            <div className="mt-4 flex flex-wrap gap-2">
              {(current.key === 'documents' || (current.key === 'review' && currentStage?.requires_client_action)) && (
                <button onClick={() => onAction('upload')} data-testid="phase-cta-upload"
                        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold bg-[#14110f] hover:bg-[#291d12] text-white px-4 py-2 rounded-md transition">
                  <UploadCloud className="w-3.5 h-3.5" /> {t('tracker.uploadDocs')}
                </button>
              )}
              {current.key === 'signature' && (
                <button onClick={() => onAction('sign')} data-testid="phase-cta-sign"
                        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold bg-[#14110f] hover:bg-[#291d12] text-white px-4 py-2 rounded-md transition">
                  <PenTool className="w-3.5 h-3.5" /> {t('tracker.reviewSign')}
                </button>
              )}
              <button onClick={() => onAction('view-tax-file')} data-testid="phase-cta-view"
                      className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold border border-neutral-200 hover:border-neutral-300 bg-white text-[#14110f] px-4 py-2 rounded-md transition">
                {t('tracker.viewTaxFile')} <ExternalLink className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulseSoft { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }
        .animate-pulse-soft { animation: pulseSoft 2.2s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
